/**
 * StatefulMacroCoordinator
 *
 * Owns the host-side macro tree walk for campaigns that have opted into the
 * stateful macro system (`HostCampaignConfig.useStatefulMacroSystem === true`).
 *
 * Architecture (Phase 3):
 * - Subscribes to `GameLaunchCoordinator.events.gameLaunched`. When the active
 *   campaign opts in, drives the walk by sending `MACRO_WALK_REQUEST` to the
 *   realtime API.
 * - On each `MACRO_WALK_RESPONSE`, runs the returned macros via
 *   `MacroManager.runMacroSteps`, requests perceptual hashes from Playjector
 *   via `IPlayjectorService.requestMacroStep`, and sends the next request.
 * - On a leaf `complete`, calls `gameLaunchCoordinator.signalLaunchMacroWalkComplete()`
 *   so the legacy `gameReady` event fires and PavilionCoordinator promotes
 *   the host to matchmakeable.
 * - On `failure` with `onFailure: 'restart'`, increments `retryCount` (held on
 *   the coordinator instance — survives `stopGame` cycles) and triggers a
 *   relaunch via `pavilionCoordinator.events.campaignConfigLoaded`. After
 *   `MACRO_WALK_MAX_RETRIES` failures, escalates via
 *   `gameLaunchCoordinator.reportCampaignSwitchFailure(...)`.
 * - On `failure` with `onFailure: null` (disabled / generic error), falls back
 *   to `gameLaunchCoordinator.executeLegacyOnLaunchMacro()`.
 *
 * Phase 1 wire-shape mapping (frozen — do NOT add fields):
 *
 *   Plan status        → Frozen wire shape
 *   --------------       -------------------
 *   step (non-leaf)    → outcome === 'continue', captures may be empty
 *                          (screenshot-optional) or non-empty (normal)
 *   step (leaf)        → outcome === 'complete'
 *   no_match restart   → outcome === 'failure', onFailure === 'restart'
 *   disabled           → outcome === 'failure', onFailure === null,
 *                          reason === DISABLED_REASON
 *   generic error      → outcome === 'failure', onFailure === null, other reason
 *
 * Anti-collision: lives entirely under `apps/host/host-frontend/`. Does NOT
 * modify `libs/Messaging/**` (Phase 1 frozen) or `apps/realtime-api/**`
 * (Phase 2 owns).
 *
 * Production-readiness Phase 5 (R13 host): when an android stream is active,
 * the capture step fans out one targeted `requestMacroStep` per live
 * instance — each instance is captured on its own monitor. Reference trees
 * stay per-campaign and walks stay per-launch; only capture/routing is
 * per-instance. The targeting logic lives in `macroStepCaptureTargeting.ts`
 * (Phase 8, review fix SF5 — file-size budget); see its locked-decision notes.
 */

import {
  generateMacroMessage,
  MacroMessageType,
  type MacroMessage,
  type MacroTrigger,
  type MacroWalkResponsePayload,
  type MacroCaptureResult,
  type WebsocketMessage,
} from '@playcastdotio/Messaging';
import type { IRealtimeService } from '../../services/realtime/IRealtimeService';
import type {
  IPlayjectorService,
  MacroStepCaptureSpec,
  MacroStepCaptureResult,
} from '../../services/playjector/interfaces/IPlayjectorService';
import type { IMessageRouter } from '../../services/MessageRouter';
import { MacroManager } from '../game-launch/managers/MacroManager';
import type { GameLaunchCoordinator } from '../game-launch/GameLaunchCoordinator';
import type { PavilionCoordinator } from '../pavilion/PavilionCoordinator';
import { machineIdState } from '../../api/stores/shared';
import { createStatefulMacroWalkStore, type StatefulMacroWalkState } from './stores';
import {
  classifyWaitCaptureError,
  extractWalkResponsePayload,
  generateLaunchInstanceId,
} from './helpers';
import { requestStepCaptures } from './macroStepCaptureTargeting';
import * as Sentry from '@sentry/react';

// ============================================================================
// Module-level constants
// ============================================================================

/**
 * Maximum number of `no_match` walk failures allowed in a single campaign
 * attempt before the host self-aborts and reports a campaign switch failure
 * to the API.
 *
 * Hardcoded per Phase 3 acceptance criteria: NO schema field, NO wire field,
 * NO admin-dashboard input. If a campaign ever needs a different value, the
 * decision is to promote it to a `MacroTreeGraph` schema field at that point
 * — not to plumb it through admin or wire today.
 */
export const MACRO_WALK_MAX_RETRIES = 3;

/**
 * Disabled-reason string the realtime-api emits when stateful macro support
 * is turned off campaign-side. Phase 2 chooses the exact wording; we treat
 * any `outcome === 'failure'` with `onFailure === null` as a fallback to the
 * legacy macro path so a wording mismatch doesn't strand the host. Reading
 * this constant in tests pins it down.
 */
export const DISABLED_REASON = 'stateful_macro_system_disabled';

/**
 * Promise-based sleep used to honor the matched node's `postMacroDelayMs`
 * between running macros and requesting Playjector captures. Pulled out
 * as a top-level helper so tests can spy on / fast-forward through it.
 */
function waitMs(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Bound for retrying continue captures while a drained native slot is still latched. */
const CONTINUE_MACRO_STEP_SLOT_RETRY_MS = 30_000;

/**
 * Maximum time we'll wait for the previous `gameClosed` event before
 * forcing the relaunch in `handleRestartFailure`. The event normally
 * fires within a second of `stopGame`; the fallback only matters when
 * Playjector hangs on teardown. Keeping the relaunch alive in that
 * case is preferred to deadlocking the retry loop forever — we still
 * own `MACRO_WALK_MAX_RETRIES` to bound total retries.
 */
const RESTART_GAMECLOSED_TIMEOUT_MS = 10_000;

/**
 * S5 — single-shot wait for `playjectorHostModeState.gameLaunched` to flip
 * to true after `handleGameLaunched` saw it false at handler entry. This
 * covers the IPC replication race where the gameLaunched event has been
 * delivered to the host renderer but the corresponding state update hasn't
 * applied yet. If the state never flips within this window, we drop the
 * walk and rely on the matchmaker safety timeout — same behavior as
 * pre-S5, just with one explicit retry first.
 */
const GAME_LAUNCHED_REQUEUE_TIMEOUT_MS = 5_000;

// ============================================================================
// Coordinator
// ============================================================================

export interface StatefulMacroCoordinatorDeps {
  realtimeService: IRealtimeService;
  playjectorService: IPlayjectorService;
  macroManager: MacroManager;
  gameLaunchCoordinator: GameLaunchCoordinator;
  pavilionCoordinator: PavilionCoordinator;
  /**
   * Production-readiness Phase 5 (R13 host): resolve the active android
   * stream + its live instance indices so macro-step captures route
   * per-instance (`{ streamId, gameInstanceIndex }`). Both optional: absent
   * (or no active stream) = the legacy aggregate capture path, so non-android
   * launches and pre-Phase-5 test harnesses are unaffected.
   */
  getActiveAndroidStreamId?: () => string | undefined;
  getLiveGameInstanceIndices?: () => number[];
}

interface InFlightWalk {
  /**
   * Snapshot of the most recent outbound MACRO_WALK_REQUEST. Replayed on
   * realtime-service reconnect so a transient WS drop doesn't strand the walk.
   */
  lastRequest: MacroMessage;
}

export class StatefulMacroCoordinator {
  private readonly realtimeService: IRealtimeService;
  private readonly playjectorService: IPlayjectorService;
  private readonly macroManager: MacroManager;
  private readonly gameLaunchCoordinator: GameLaunchCoordinator;
  private readonly pavilionCoordinator: PavilionCoordinator;
  private readonly getActiveAndroidStreamId?: () => string | undefined;
  private readonly getLiveGameInstanceIndices?: () => number[];

  private readonly walkStore = createStatefulMacroWalkStore();

  // retryCount lives on the coordinator instance (NOT the walk state) so it
  // survives the `stopGame` / `gameLaunched` cycle that a `restart` triggers.
  // Reset only when a different campaign is detected on a fresh launch.
  private retryCount = 0;
  private retryCampaignId: string | null = null;
  private waitPollIndex = 0;

  /** Long-wait guard: true while inside a postMacroDelayMs settle wait */
  private _isWaitingOutSettle = false;

  private inFlight: InFlightWalk | null = null;
  /**
   * True while an accepted MACRO_WALK_RESPONSE is still being processed
   * (including Wait sleep/capture). Duplicate responses are dropped.
   * `responseGeneration` lets `sendWalkRequest` detach the producer
   * handler so its `finally` cannot clobber a later response.
   */
  private processingWalkResponse = false;
  private responseGeneration = 0;
  private initialized = false;

  // S5: dispose handle for an in-flight gameLaunched-requeue subscription.
  // Set when `handleGameLaunched` short-circuits on stale state and arms a
  // one-shot listener; cleared when the listener fires, the timeout
  // expires, or `destroy()` runs. Non-null implies "we're already waiting
  // for the next gameLaunched=true edge — don't arm a second listener".
  private pendingGameLaunchedRequeue: (() => void) | null = null;

  // Stored unsubscribe handles so `destroy()` can clean up cleanly in tests.
  private readonly disposers: Array<() => void> = [];

  constructor(deps: StatefulMacroCoordinatorDeps) {
    this.realtimeService = deps.realtimeService;
    this.playjectorService = deps.playjectorService;
    this.macroManager = deps.macroManager;
    this.gameLaunchCoordinator = deps.gameLaunchCoordinator;
    this.pavilionCoordinator = deps.pavilionCoordinator;
    this.getActiveAndroidStreamId = deps.getActiveAndroidStreamId;
    this.getLiveGameInstanceIndices = deps.getLiveGameInstanceIndices;
  }

  // ==========================================================================
  // Public API
  // ==========================================================================

  /**
   * Wire up event subscriptions. Idempotent: safe to call multiple times.
   * Coordinator does not establish a realtime connection on its own — it
   * cooperates with the existing realtimeService lifecycle and registers a
   * MACRO_WALK_RESPONSE handler that's safe to call before the WS comes up.
   */
  public init(): void {
    if (this.initialized) {
      console.log('[StatefulMacroSystemStep] init: already initialized — no-op');
      return;
    }
    this.initialized = true;

    const unsubscribeGameLaunched = this.gameLaunchCoordinator.events.gameLaunched.addListener(() => {
      this.handleGameLaunched();
    });
    this.disposers.push(unsubscribeGameLaunched);

    const unsubscribeReconnect = this.realtimeService.onConnected(() => {
      this.handleRealtimeReconnect();
    });
    this.disposers.push(unsubscribeReconnect);

    console.log('[StatefulMacroSystemStep] ✅ Initialized');
  }

  /**
   * Register the MACRO_WALK_RESPONSE handler with the realtime message router.
   * Separate from `init()` so call sites can mirror the existing pattern
   * (PavilionCoordinator does the same with `registerMessageHandlers`).
   */
  public registerMessageHandlers(router: IMessageRouter): void {
    router.register(MacroMessageType.MACRO_WALK_RESPONSE, (msg: WebsocketMessage) => {
      // The router dispatches by message.type, so we know the type matches —
      // but defensively narrow before handing off the discriminated union.
      if (msg.type !== MacroMessageType.MACRO_WALK_RESPONSE) {
        console.warn('[StatefulMacroSystemStep] Received non-MACRO_WALK_RESPONSE on macro handler — dropping');
        return;
      }
      void this.handleMacroWalkResponse(msg as MacroMessage);
    });
  }

  /**
   * Read-only inspector. Returns a snapshot of the live walk state plus the
   * persisted `retryCount` (which lives on the coordinator instance, not on
   * the per-walk store).
   */
  public get state(): StatefulMacroWalkState {
    return {
      ...this.walkStore.currentState,
      retryCount: this.retryCount,
    };
  }

  /**
   * Long-wait guard: returns true while the walk is inside a `postMacroDelayMs`
   * settle wait. CampaignSwitchManager checks this signal before treating a
   * missing `gameReady` as a timeout.
   */
  public isWaitingOutSettle(): boolean {
    return this._isWaitingOutSettle;
  }

  /**
   * Test helper: tear down event subscriptions. Production code does not call
   * this — the coordinator is a singleton from `init.ts`. Idempotent.
   */
  public destroy(): void {
    for (const d of this.disposers) {
      try {
        d();
      } catch (err) {
        console.error('[StatefulMacroSystemStep] Error in disposer', err);
      }
    }
    this.disposers.length = 0;
    if (this.pendingGameLaunchedRequeue) {
      try { this.pendingGameLaunchedRequeue(); } catch { /* ignore */ }
      this.pendingGameLaunchedRequeue = null;
    }
    this.initialized = false;
    this.inFlight = null;
    this.processingWalkResponse = false;
    this.responseGeneration += 1;
    this.playjectorService.abandonMacroStep();
  }

  // ==========================================================================
  // Walk loop
  // ==========================================================================

  private handleGameLaunched(): void {
    const cfg = this.pavilionCoordinator.getCampaignConfig();
    if (!cfg?.useStatefulMacroSystem) {
      // Default path — GameLaunchCoordinator already runs the legacy macro and
      // emits gameReady on its own.
      return;
    }

    const campaignId = cfg.campaignId;
    if (!campaignId) {
      console.warn('[StatefulMacroSystemStep] gameLaunched with stateful macros but no campaignId — bailing to legacy');
      void this.gameLaunchCoordinator.executeLegacyOnLaunchMacro();
      return;
    }

    // Bug 2B / S5 — re-check Playjector's reported launch state immediately
    // before kicking off the walk. The `gameLaunched` event signals that
    // the game IS launched, but the IPC replication race can deliver the
    // event before the corresponding state update applies on the renderer
    // (or a rapid stop/launch cycle from the restart-failure path leaves
    // the state oscillating). Sending MACRO_WALK_REQUEST against a stale
    // state results in "process not ready" on the first macro step.
    //
    // S5: instead of dropping the event outright, arm a single one-shot
    // listener that re-runs this handler when the state flips to
    // gameLaunched === true (with a hard timeout). Falls back to the
    // matchmaker safety net if the requeue never fires.
    const playjectorState = this.playjectorService.getCurrentState();
    if (!playjectorState.gameLaunched) {
      this.requeueOnGameLaunched(campaignId);
      return;
    }

    // Reaching this point means state was true at handler entry — clear any
    // pending requeue listener; it would fire spuriously on a future flip.
    if (this.pendingGameLaunchedRequeue) {
      try { this.pendingGameLaunchedRequeue(); } catch { /* ignore */ }
      this.pendingGameLaunchedRequeue = null;
    }

    if (this.retryCampaignId !== campaignId) {
      // Different campaign than the one that accumulated retries — reset.
      this.retryCount = 0;
      this.retryCampaignId = campaignId;
    }

    // Tear down any previous walk (and free a latched native capture slot)
    // before starting a new launch instance.
    this.deactivateWalk();

    const trigger: MacroTrigger = 'launch';
    const launchInstanceId = generateLaunchInstanceId();

    this.walkStore.updateState({
      active: true,
      trigger,
      currentNodeId: null,
      retryCount: this.retryCount,
      launchInstanceId,
      waitNodeId: null,
      waitDeadlineMs: null,
    });

    console.log('[StatefulMacroSystemStep] 🚀 Starting macro walk', {
      campaignId,
      trigger,
      launchInstanceId,
      retryCount: this.retryCount,
    });

    this.sendWalkRequest({
      campaignId,
      trigger,
      launchInstanceId,
      currentNodeId: null,
      captures: [],
    });
  }

  private async handleMacroWalkResponse(message: MacroMessage): Promise<void> {
    if (message.type !== MacroMessageType.MACRO_WALK_RESPONSE) return;

    const payload = extractWalkResponsePayload(message);

    console.log('[StatefulMacroSystemStep] 📥 MACRO_WALK_RESPONSE received', {
      outcome: payload.outcome,
      nextNodeId: payload.nextNodeId,
      captureCount: payload.captures.length,
      macroCount: payload.macros.length,
      launchInstanceId: payload.launchInstanceId,
      onFailure: payload.onFailure,
      reason: payload.reason ?? null,
    });

    const expected = this.walkStore.currentState.launchInstanceId;
    if (!expected || payload.launchInstanceId !== expected) {
      console.warn('[StatefulMacroSystemStep] 🪦 Dropping stale MACRO_WALK_RESPONSE', {
        expected,
        received: payload.launchInstanceId,
      });
      return;
    }

    if (!this.walkStore.currentState.active) {
      console.warn('[StatefulMacroSystemStep] Received MACRO_WALK_RESPONSE while inactive — dropping', {
        launchInstanceId: payload.launchInstanceId,
      });
      return;
    }

    if (this.processingWalkResponse) {
      console.warn('[StatefulMacroSystemStep] Dropping concurrent MACRO_WALK_RESPONSE — a walk step is already in progress', {
        launchInstanceId: payload.launchInstanceId,
        outcome: payload.outcome,
        nextNodeId: payload.nextNodeId,
      });
      return;
    }

    // This response answers the current request. Clear inFlight so a
    // realtime reconnect cannot replay it while Wait is still sleeping
    // or capturing (that replay would start a second Wait loop).
    const generation = ++this.responseGeneration;
    this.processingWalkResponse = true;
    this.inFlight = null;
    try {
      switch (payload.outcome) {
        case 'continue':
          await this.handleContinueOutcome(payload);
          return;
        case 'complete':
          await this.handleCompleteOutcome(payload);
          return;
        case 'failure':
          await this.handleFailureOutcome(payload);
          return;
        default: {
          const exhaustive: never = payload.outcome;
          console.error('[StatefulMacroSystemStep] Unhandled outcome', exhaustive);
        }
      }
    } finally {
      if (generation === this.responseGeneration) {
        this.processingWalkResponse = false;
      }
    }
  }

  private async handleContinueOutcome(payload: MacroWalkResponsePayload): Promise<void> {
    if (payload.waitPoll) {
      await this.handleWaitContinueOutcome(payload);
      return;
    }

    this.clearWaitState();
    console.log('[StatefulMacroSystemStep] 🟢 step (non-leaf) — running macros + requesting captures');

    try {
      await this.macroManager.runMacroSteps(payload.macros, 'StatefulMacro');
    } catch (err) {
      console.error('[StatefulMacroSystemStep] runMacroSteps threw on continue path:', {
        campaignId: payload.campaignId,
        launchInstanceId: payload.launchInstanceId,
        nextNodeId: payload.nextNodeId,
        macroCount: payload.macros.length,
        error: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? err.stack : undefined,
      });
      this.fallbackToLegacy('runMacroSteps_threw');
      return;
    }

    // S4: launch-instance check after macro execution. Macros can take a
    // non-trivial amount of time to play out (multi-step gpad sequences,
    // typing strings, etc.). A campaign switch during that window means
    // any subsequent capture would be against the new game's screen, not
    // the one we expected. Catches the gap that the postMacroDelayMs > 0
    // re-check below would otherwise miss when delay is 0.
    if (this.walkStore.currentState.launchInstanceId !== payload.launchInstanceId) {
      console.warn('[StatefulMacroSystemStep] Launch instance changed during runMacroSteps — abandoning step', {
        payloadLaunchInstanceId: payload.launchInstanceId,
        currentLaunchInstanceId: this.walkStore.currentState.launchInstanceId,
      });
      return;
    }

    // Settle delay: lets transitions (fades, animated menus) finish before
    // we ask Playjector for the screenshot. Sourced from the matched
    // node's `postMacroDelayMs`. Older API responses may omit it; the
    // schema's default is 0, so missing means "no delay".
    const delayMs = payload.postMacroDelayMs ?? 0;
    if (delayMs > 0) {
      console.log('[StatefulMacroSystemStep] ⏳ Waiting postMacroDelayMs before requesting capture', {
        campaignId: payload.campaignId,
        launchInstanceId: payload.launchInstanceId,
        nextNodeId: payload.nextNodeId,
        postMacroDelayMs: delayMs,
      });
      this._isWaitingOutSettle = true;
      await waitMs(delayMs);
      this._isWaitingOutSettle = false;

      // Mid-delay launch-instance check. If the user closed the game or
      // restarted the campaign during the wait, abandon the rest of this
      // step — `requestMacroStep` against a dead Playjector would either
      // hang on the IPC timeout or return stale data.
      const stillCurrentAfterDelay = this.walkStore.currentState.launchInstanceId === payload.launchInstanceId;
      if (!stillCurrentAfterDelay) {
        console.warn('[StatefulMacroSystemStep] Launch instance changed during postMacroDelayMs — abandoning step', {
          payloadLaunchInstanceId: payload.launchInstanceId,
          currentLaunchInstanceId: this.walkStore.currentState.launchInstanceId,
        });
        return;
      }
    }

    // Screenshot-optional path: when the API returns continue with no
    // capture specs, the matched node's children don't need hash
    // verification (solo-child without expectedState). Skip the
    // Playjector IPC and advance immediately with empty captures.
    if (payload.captures.length === 0) {
      console.log('[StatefulMacroSystemStep] 📸 Continue with empty captures — screenshot-optional node, skipping requestMacroStep', {
        campaignId: payload.campaignId,
        launchInstanceId: payload.launchInstanceId,
        nextNodeId: payload.nextNodeId,
      });

      this.walkStore.updateState({
        ...this.walkStore.currentState,
        currentNodeId: payload.nextNodeId,
      });

      this.sendWalkRequest({
        campaignId: payload.campaignId,
        trigger: payload.trigger,
        launchInstanceId: payload.launchInstanceId,
        currentNodeId: payload.nextNodeId,
        captures: [],
      });
      return;
    }

    const capturesByCaptureId: Record<string, MacroStepCaptureSpec> = {};
    for (const spec of payload.captures) {
      capturesByCaptureId[spec.captureId] = { width: spec.width, height: spec.height };
    }

    let hashes: Record<string, MacroStepCaptureResult> | 'stale';
    try {
      hashes = await requestStepCaptures(
        {
          playjectorService: this.playjectorService,
          getActiveAndroidStreamId: this.getActiveAndroidStreamId,
          getLiveGameInstanceIndices: this.getLiveGameInstanceIndices,
          requestStep: (captures, target) =>
            this.requestMacroStepForCurrentLaunch(captures, payload.launchInstanceId, target),
        },
        capturesByCaptureId,
      );
    } catch (err) {
      console.error('[StatefulMacroSystemStep] requestMacroStep (Playjector IPC) failed:', {
        campaignId: payload.campaignId,
        launchInstanceId: payload.launchInstanceId,
        captureCount: payload.captures.length,
        captureIds: Object.keys(capturesByCaptureId),
        error: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? err.stack : undefined,
      });
      this.fallbackToLegacy('requestMacroStep_failed');
      return;
    }
    if (hashes === 'stale') {
      return;
    }

    // Stale-launch guard: another launch may have started while we were
    // awaiting the IPC reply. Drop the response if so.
    const stillCurrent = this.walkStore.currentState.launchInstanceId === payload.launchInstanceId;
    if (!stillCurrent) {
      console.warn('[StatefulMacroSystemStep] Launch instance changed mid-step — dropping captured hashes', {
        payloadLaunchInstanceId: payload.launchInstanceId,
        currentLaunchInstanceId: this.walkStore.currentState.launchInstanceId,
      });
      return;
    }

    this.walkStore.updateState({
      ...this.walkStore.currentState,
      currentNodeId: payload.nextNodeId,
    });

    const nextCaptures: MacroCaptureResult[] = Object.entries(hashes).map(([captureId, result]) => ({
      captureId,
      hash: result.hash,
      width: result.width,
      height: result.height,
    }));

    this.sendWalkRequest({
      campaignId: payload.campaignId,
      trigger: payload.trigger,
      launchInstanceId: payload.launchInstanceId,
      currentNodeId: payload.nextNodeId,
      captures: nextCaptures,
    });
  }

  private async handleWaitContinueOutcome(payload: MacroWalkResponsePayload): Promise<void> {
    const nodeId = payload.nextNodeId;
    const waitPoll = payload.waitPoll;
    if (!nodeId || !waitPoll) {
      this.fallbackToLegacy('invalid_wait_poll_payload');
      return;
    }

    const state = this.walkStore.currentState;
    const firstEnter = state.waitNodeId !== nodeId || state.waitDeadlineMs === null;
    if (firstEnter) {
      this.waitPollIndex = 0;
      this.walkStore.updateState({
        ...state,
        waitNodeId: nodeId,
        waitDeadlineMs: Date.now() + waitPoll.timeoutMs,
      });
    }

    try {
      await this.macroManager.runMacroSteps(payload.macros, 'StatefulMacro:wait');
    } catch (err) {
      console.error('[StatefulMacroSystemStep] runMacroSteps threw on Wait path:', err);
      this.fallbackToLegacy('wait_runMacroSteps_threw');
      return;
    }

    if (!this.isCurrentLaunch(payload.launchInstanceId)) return;

    if (!firstEnter) {
      if (this.hasWaitDeadlineElapsed()) {
        await this.restartWait(payload, 'wait_timeout');
        return;
      }
      await waitMs(waitPoll.intervalMs);
      if (!this.isCurrentLaunch(payload.launchInstanceId)) return;
    }

    await this.captureWaitUntilRequestOrTerminal(payload);
  }

  private async captureWaitUntilRequestOrTerminal(payload: MacroWalkResponsePayload): Promise<void> {
    const waitPoll = payload.waitPoll;
    if (!waitPoll) return;

    while (this.isCurrentLaunch(payload.launchInstanceId)) {
      if (this.hasWaitDeadlineElapsed()) {
        await this.restartWait(payload, 'wait_timeout');
        return;
      }

      this.waitPollIndex += 1;
      const deadlineMs = this.walkStore.currentState.waitDeadlineMs;
      console.log('[StatefulMacroSystemStep] Wait capture', {
        pollIndex: this.waitPollIndex,
        elapsedMs: deadlineMs === null ? 0 : Date.now() - (deadlineMs - waitPoll.timeoutMs),
        nextNodeId: payload.nextNodeId,
        launchInstanceId: payload.launchInstanceId,
      });

      if (payload.captures.length === 0) {
        await waitMs(waitPoll.intervalMs);
        continue;
      }

      const capturesByCaptureId = this.buildCaptureSpecs(payload);
      let hashes: Record<string, MacroStepCaptureResult>;
      try {
        hashes = await this.playjectorService.requestMacroStep(capturesByCaptureId);
      } catch (err) {
        if (!this.isCurrentLaunch(payload.launchInstanceId)) return;
        const action = classifyWaitCaptureError(err);
        if (action === 'restart') {
          await this.restartWait(payload, 'wait_no_process');
          return;
        }
        if (action === 'legacy') {
          this.fallbackToLegacy('wait_requestMacroStep_failed');
          return;
        }
        if (this.hasWaitDeadlineElapsed()) {
          await this.restartWait(payload, 'wait_timeout');
          return;
        }
        await waitMs(waitPoll.intervalMs);
        continue;
      }

      if (!this.isCurrentLaunch(payload.launchInstanceId)) return;
      const hashEntries = Object.entries(hashes);
      if (hashEntries.length === 0) {
        this.fallbackToLegacy('wait_empty_variant_results');
        return;
      }

      this.walkStore.updateState({
        ...this.walkStore.currentState,
        currentNodeId: payload.nextNodeId,
      });
      this.sendWalkRequest({
        campaignId: payload.campaignId,
        trigger: payload.trigger,
        launchInstanceId: payload.launchInstanceId,
        currentNodeId: payload.nextNodeId,
        captures: hashEntries.map(([captureId, result]) => ({
          captureId,
          hash: result.hash,
          width: result.width,
          height: result.height,
        })),
      });
      return;
    }
  }

  private buildCaptureSpecs(payload: MacroWalkResponsePayload): Record<string, MacroStepCaptureSpec> {
    return Object.fromEntries(
      payload.captures.map((spec) => [
        spec.captureId,
        { width: spec.width, height: spec.height },
      ]),
    );
  }

  private hasWaitDeadlineElapsed(): boolean {
    const deadlineMs = this.walkStore.currentState.waitDeadlineMs;
    return deadlineMs !== null && Date.now() >= deadlineMs;
  }

  private isCurrentLaunch(launchInstanceId: string): boolean {
    return this.walkStore.currentState.launchInstanceId === launchInstanceId;
  }

  private async restartWait(payload: MacroWalkResponsePayload, reason: string): Promise<void> {
    await this.handleRestartFailure({
      ...payload,
      outcome: 'failure',
      nextNodeId: null,
      macros: [],
      captures: [],
      onFailure: 'restart',
      reason,
      waitPoll: undefined,
    });
  }

  private async handleCompleteOutcome(payload: MacroWalkResponsePayload): Promise<void> {
    console.log('[StatefulMacroSystemStep] 🏁 step (leaf) — walk complete');

    if (payload.macros.length > 0) {
      try {
        await this.macroManager.runMacroSteps(payload.macros, 'StatefulMacro:leaf');
      } catch (err) {
        console.error('[StatefulMacroSystemStep] runMacroSteps threw on complete path:', err);
        // Continue to signal walk complete: leaf macros are best-effort. The
        // game IS launched at this point; failing to type a single key
        // shouldn't strand the host as un-matchmakeable.
      }
    }

    // S4: launch-instance check after leaf macros. If the campaign was
    // switched while we were running the leaf macros, do NOT signal
    // walk-complete for the old launch — `signalLaunchMacroWalkComplete`
    // is idempotent per launch on the GameLaunchCoordinator side, but
    // emitting it here would also fire a "matchmaking-ready" beacon for
    // a launch that's already been superseded.
    if (this.walkStore.currentState.launchInstanceId !== payload.launchInstanceId) {
      console.warn('[StatefulMacroSystemStep] Launch instance changed during leaf runMacroSteps — skipping walk-complete signal', {
        payloadLaunchInstanceId: payload.launchInstanceId,
        currentLaunchInstanceId: this.walkStore.currentState.launchInstanceId,
      });
      return;
    }

    this.deactivateWalk();
    this.retryCount = 0;
    this.retryCampaignId = null;

    if (payload.trigger === 'launch') {
      this.gameLaunchCoordinator.signalLaunchMacroWalkComplete();
    }
  }

  private async handleFailureOutcome(payload: MacroWalkResponsePayload): Promise<void> {
    if (payload.onFailure === 'restart') {
      await this.handleRestartFailure(payload);
      return;
    }

    // onFailure === null — either the API has explicitly disabled the stateful
    // macro system for this campaign, or it's reporting a generic error. In
    // both cases the host falls back to the legacy onLaunchMacro path.
    if (payload.reason === DISABLED_REASON) {
      console.log('[StatefulMacroSystemStep] 🟡 disabled — falling back to legacy onLaunchMacro', {
        campaignId: payload.campaignId,
        launchInstanceId: payload.launchInstanceId,
      });
    } else {
      console.error('[StatefulMacroSystemStep] 🔴 error — falling back to legacy onLaunchMacro', {
        campaignId: payload.campaignId,
        launchInstanceId: payload.launchInstanceId,
        reason: payload.reason ?? null,
      });
    }
    this.fallbackToLegacy(payload.reason ?? 'walk_failure');
  }

  private async handleRestartFailure(payload: MacroWalkResponsePayload): Promise<void> {
    this.retryCount += 1;
    this.retryCampaignId = payload.campaignId;
    const restartContext = {
      retryCount: this.retryCount,
      max: MACRO_WALK_MAX_RETRIES,
      reason: payload.reason,
      campaignId: payload.campaignId,
      launchInstanceId: payload.launchInstanceId,
    };
    console.warn('[StatefulMacroSystemStep] 🔁 restart triggered', restartContext);
    Sentry.logger.warn('[StatefulMacroSystemStep] 🔁 restart triggered', restartContext);

    if (this.retryCount > MACRO_WALK_MAX_RETRIES) {
      console.error(
        '[StatefulMacroSystemStep] ❌ macro walk max retries exceeded — escalating campaign failure',
        restartContext,
      );

      Sentry.logger.error(
        '[StatefulMacroSystemStep] ❌ macro walk max retries exceeded — escalating campaign failure',
        restartContext,
      );

      this.deactivateWalk();
      // Terminate the lingering game from the final failed attempt. Without
      // this, `reportCampaignSwitchFailure` only emits an event —
      // `PavilionCoordinator.handleCampaignSwitchFailure` updates state and
      // notifies the API but never calls `stopGame`, leaving the broken game
      // window on screen. Fire-and-forget (no gameClosed await) because we're
      // not relaunching; `stopGame` is a no-op when no game is running.
      this.gameLaunchCoordinator.stopGame(this.pavilionCoordinator.getAuthId());
      this.gameLaunchCoordinator.reportCampaignSwitchFailure(
        payload.campaignId,
        'macro_walk_max_retries',
        false,
      );
      // Reset for the next campaign attempt; the campaign that just failed
      // will be cleared by PavilionCoordinator.
      this.retryCount = 0;
      this.retryCampaignId = null;
      return;
    }

    this.deactivateWalk();

    // Mirror the existing relaunch path: stop the running game and re-emit
    // campaignConfigLoaded so GameLaunchCoordinator launches it again. The
    // next `gameLaunched` event will start a fresh walk.
    //
    // Bug 2A — wait for the OLD game's `gameClosed` event before emitting
    // campaignConfigLoaded for the relaunch. Without this, Playjector can
    // still be tearing down the previous process when the new launch
    // arrives, leading to "process not ready" on the very first
    // post-relaunch macro step. The timeout fallback keeps the retry loop
    // alive when teardown hangs (we still bound total retries with
    // MACRO_WALK_MAX_RETRIES — see the gate above).
    const authId = this.pavilionCoordinator.getAuthId();

    const gameClosedPromise = new Promise<'closed' | 'timeout'>((resolve) => {
      let settled = false;
      const unsubscribe = this.gameLaunchCoordinator.events.gameClosed.addListener(() => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        unsubscribe();
        resolve('closed');
      });
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        unsubscribe();
        resolve('timeout');
      }, RESTART_GAMECLOSED_TIMEOUT_MS);
    });

    // Tell PavilionCoordinator we're mid-retry BEFORE issuing stopGame. The
    // gameClosed listener on PavilionCoordinator otherwise treats this as a
    // normal close, clears campaignId on hostMatchmakingStore, and pushes
    // HOST_CONFIG with no campaign to the API — which makes admin-dash show
    // "no campaign assigned" for the entire retry window even though the
    // campaign IS about to relaunch with the same id. The flag is cleared
    // automatically on the next gameLaunched (see handleGameLaunched).
    this.pavilionCoordinator.beginMacroRetryRestart();
    this.gameLaunchCoordinator.stopGame(authId);

    const closeOutcome = await gameClosedPromise;
    if (closeOutcome === 'timeout') {
      console.warn(
        '[StatefulMacroSystemStep] ⏱ gameClosed not observed before timeout — relaunching anyway',
        { ...restartContext, timeoutMs: RESTART_GAMECLOSED_TIMEOUT_MS },
      );

      Sentry.logger.warn(
        '[StatefulMacroSystemStep] ⏱ gameClosed not observed before timeout — relaunching anyway',
        { ...restartContext, timeoutMs: RESTART_GAMECLOSED_TIMEOUT_MS },
      );
    } else {
      console.log('[StatefulMacroSystemStep] gameClosed observed — issuing relaunch', restartContext);
      Sentry.logger.info('[StatefulMacroSystemStep] gameClosed observed — issuing relaunch', restartContext);
    }

    this.pavilionCoordinator.events.campaignConfigLoaded.emit({ campaignId: payload.campaignId });
  }

  // ==========================================================================
  // Realtime reconnect
  // ==========================================================================

  /**
   * S5: arm a single-shot listener for the next gameLaunched=true edge,
   * then re-run `handleGameLaunched`. No-op if a listener is already
   * armed (don't pile up subscriptions on flapping state).
   */
  private requeueOnGameLaunched(campaignId: string): void {
    if (this.pendingGameLaunchedRequeue) {
      console.log('[StatefulMacroSystemStep] handleGameLaunched: requeue already pending — coalescing', {
        campaignId,
        retryCount: this.retryCount,
      });
      return;
    }

    console.warn(
      '[StatefulMacroSystemStep] handleGameLaunched: state.gameLaunched is false at handler entry — arming one-shot requeue',
      {
        campaignId,
        retryCount: this.retryCount,
        timeoutMs: GAME_LAUNCHED_REQUEUE_TIMEOUT_MS,
      },
    );

    let settled = false;
    const cleanup = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      unsubscribe();
      this.pendingGameLaunchedRequeue = null;
    };

    const unsubscribe = this.playjectorService.onStateChange((state) => {
      if (settled) return;
      if (!state.gameLaunched) return;
      cleanup();
      console.log('[StatefulMacroSystemStep] handleGameLaunched: state.gameLaunched flipped to true — re-running handler', {
        campaignId,
      });
      this.handleGameLaunched();
    });

    const timer = setTimeout(() => {
      if (settled) return;
      cleanup();
      console.warn(
        '[StatefulMacroSystemStep] handleGameLaunched: requeue timed out without state.gameLaunched=true — dropping walk attempt',
        {
          campaignId,
          retryCount: this.retryCount,
          timeoutMs: GAME_LAUNCHED_REQUEUE_TIMEOUT_MS,
        },
      );
    }, GAME_LAUNCHED_REQUEUE_TIMEOUT_MS);

    this.pendingGameLaunchedRequeue = cleanup;
  }

  private handleRealtimeReconnect(): void {
    if (!this.inFlight || !this.walkStore.currentState.active) return;
    console.log('[StatefulMacroSystemStep] 🌐 Realtime reconnected — replaying in-flight MACRO_WALK_REQUEST');
    // Reuse `safeSend` so a synchronous throw on this replay path
    // gets the same fallback-to-legacy treatment as the original
    // request — without it, a sync throw escapes through the
    // realtime-service event callback and leaves the walk active
    // with no recovery (the host hangs until the socket eventually
    // disconnects again).
    this.safeSend(this.inFlight.lastRequest, 'reconnect_replay_send_failed');
  }

  // ==========================================================================
  // Helpers
  // ==========================================================================

  private sendWalkRequest(args: {
    campaignId: string;
    trigger: MacroTrigger;
    launchInstanceId: string;
    currentNodeId: string | null;
    captures: MacroCaptureResult[];
  }): void {
    const machineId = machineIdState.currentState;
    if (!machineId) {
      console.warn('[StatefulMacroSystemStep] No machineId — cannot send MACRO_WALK_REQUEST');
      this.fallbackToLegacy('no_machine_id');
      return;
    }

    const message = generateMacroMessage(MacroMessageType.MACRO_WALK_REQUEST, {
      machineId,
      campaignId: args.campaignId,
      trigger: args.trigger,
      launchInstanceId: args.launchInstanceId,
      currentNodeId: args.currentNodeId,
      captures: args.captures,
    });

    const requestSummary = {
      campaignId: args.campaignId,
      trigger: args.trigger,
      currentNodeId: args.currentNodeId,
      captureCount: args.captures.length,
      launchInstanceId: args.launchInstanceId,
    };
    console.log('[StatefulMacroSystemStep] 📤 Sending MACRO_WALK_REQUEST', requestSummary);

    // The next response belongs to this new request. Release the response
    // lock and invalidate the producer handler's `finally` so a fast Wait
    // miss continue is not dropped as "concurrent".
    this.responseGeneration += 1;
    this.processingWalkResponse = false;
    this.inFlight = { lastRequest: message };
    this.safeSend(message, 'send_failed', requestSummary);
  }

  /**
   * Wrap `realtimeService.sendMessage` with the standard sync-throw fallback.
   * Both the initial request path and the reconnect-replay path go through
   * here so a sync throw is never silently swallowed by the caller's event
   * source.
   */
  private safeSend(
    message: MacroMessage,
    fallbackReason: string,
    extraLogContext: Record<string, unknown> = {},
  ): void {
    try {
      this.realtimeService.sendMessage(message);
    } catch (err) {
      console.error('[StatefulMacroSystemStep] ❌ Failed to send MACRO_WALK_REQUEST — falling back to legacy', {
        ...extraLogContext,
        fallbackReason,
        error: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? err.stack : undefined,
      });
      this.fallbackToLegacy(fallbackReason);
    }
  }

  private deactivateWalk(): void {
    this.walkStore.updateState({
      ...this.walkStore.currentState,
      active: false,
      trigger: null,
      currentNodeId: null,
      launchInstanceId: null,
      waitNodeId: null,
      waitDeadlineMs: null,
    });
    this.inFlight = null;
    this.processingWalkResponse = false;
    this.responseGeneration += 1;
    this.waitPollIndex = 0;
    this.playjectorService.abandonMacroStep();
  }

  private async requestMacroStepForCurrentLaunch(
    capturesByCaptureId: Record<string, MacroStepCaptureSpec>,
    launchInstanceId: string,
    target?: { streamId: string; gameInstanceIndex: number },
  ): Promise<Record<string, MacroStepCaptureResult> | 'stale'> {
    const retryUntil = Date.now() + CONTINUE_MACRO_STEP_SLOT_RETRY_MS;
    while (true) {
      if (this.walkStore.currentState.launchInstanceId !== launchInstanceId) {
        return 'stale';
      }
      try {
        return await this.playjectorService.requestMacroStep(capturesByCaptureId, undefined, target);
      } catch (err) {
        if (this.walkStore.currentState.launchInstanceId !== launchInstanceId) {
          return 'stale';
        }
        const action = classifyWaitCaptureError(err);
        if (action === 'retry' && Date.now() < retryUntil) {
          await waitMs(250);
          continue;
        }
        throw err;
      }
    }
  }

  private clearWaitState(): void {
    const state = this.walkStore.currentState;
    if (state.waitNodeId === null && state.waitDeadlineMs === null) return;
    this.walkStore.updateState({
      ...state,
      waitNodeId: null,
      waitDeadlineMs: null,
    });
    this.waitPollIndex = 0;
  }

  private fallbackToLegacy(reason: string): void {
    console.log('[StatefulMacroSystemStep] ⤴️  Falling back to legacy onLaunchMacro path:', reason);
    this.deactivateWalk();
    this.retryCount = 0;
    this.retryCampaignId = null;
    void this.gameLaunchCoordinator.executeLegacyOnLaunchMacro();
  }
}

